/**
 * @fileoverview Hook personalizado para monitoreo OAuth2
 * 
 * Proporciona una interfaz React para el sistema de monitoreo OAuth2,
 * con estado reactivo y métodos de utilidad.
 * Implementa principios de hooks personalizados y separación de responsabilidades.
 * 
 * @author Nubelytics Engineering Team
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import oauth2Monitor from '../infrastructure/monitoring/OAuth2Monitor';

/**
 * Hook personalizado para monitorear el estado OAuth2
 * @returns {Object} Estado y métodos del monitor OAuth2
 */
export const useOAuth2Monitor = () => {
  const [metrics, setMetrics] = useState({});
  const [diagnostics, setDiagnostics] = useState({});
  const [isHealthy, setIsHealthy] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  /**
   * Actualiza el estado del monitor
   */
  const updateState = useCallback(() => {
    const currentMetrics = oauth2Monitor.getMetrics();
    const currentDiagnostics = oauth2Monitor.getDiagnostics();
    
    setMetrics(currentMetrics);
    setDiagnostics(currentDiagnostics);
    setIsHealthy(currentMetrics.systemHealth === 'healthy');
    setLastUpdate(new Date());
  }, []);

  /**
   * Configura listeners del monitor
   */
  useEffect(() => {
    // Actualización inicial
    updateState();

    // Event listeners
    const handleTokenRequest = () => updateState();
    const handleNetworkRequest = () => updateState();
    const handleAuthError = () => updateState();
    const handleHealthChange = (health) => {
      setIsHealthy(health === 'healthy');
      updateState();
    };

    oauth2Monitor.on('tokenRequest', handleTokenRequest);
    oauth2Monitor.on('networkRequest', handleNetworkRequest);
    oauth2Monitor.on('authError', handleAuthError);
    oauth2Monitor.on('healthChange', handleHealthChange);

    // Cleanup
    return () => {
      oauth2Monitor.off('tokenRequest', handleTokenRequest);
      oauth2Monitor.off('networkRequest', handleNetworkRequest);
      oauth2Monitor.off('authError', handleAuthError);
      oauth2Monitor.off('healthChange', handleHealthChange);
    };
  }, [updateState]);

  /**
   * Exporta diagnósticos como JSON
   */
  const exportDiagnostics = useCallback(() => {
    return oauth2Monitor.exportDiagnostics();
  }, []);

  /**
   * Descarga diagnósticos como archivo
   */
  const downloadDiagnostics = useCallback(() => {
    const diagnosticsData = oauth2Monitor.exportDiagnostics();
    const blob = new Blob([diagnosticsData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oauth2-diagnostics-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  /**
   * Obtiene estadísticas resumidas
   */
  const getStats = useCallback(() => {
    return {
      totalRequests: metrics.tokenRequests || 0,
      totalRefreshes: metrics.tokenRefreshes || 0,
      totalErrors: metrics.authErrors || 0,
      errorRate: metrics.errorRate || 0,
      consecutiveErrors: metrics.consecutiveErrors || 0,
      systemHealth: metrics.systemHealth || 'unknown',
      lastTokenRefresh: metrics.lastTokenRefresh,
      lastAuthError: metrics.lastAuthError
    };
  }, [metrics]);

  /**
   * Obtiene errores recientes
   */
  const getRecentErrors = useCallback((limit = 5) => {
    return diagnostics.errorHistory?.slice(-limit) || [];
  }, [diagnostics]);

  /**
   * Obtiene solicitudes de red recientes
   */
  const getRecentNetworkRequests = useCallback((limit = 10) => {
    return diagnostics.networkRequests?.slice(-limit) || [];
  }, [diagnostics]);

  /**
   * Verifica si el sistema está en estado crítico
   */
  const isCritical = useCallback(() => {
    return metrics.systemHealth === 'critical';
  }, [metrics]);

  /**
   * Verifica si hay advertencias
   */
  const hasWarnings = useCallback(() => {
    return metrics.systemHealth === 'warning' || metrics.consecutiveErrors > 0;
  }, [metrics]);

  /**
   * Obtiene el color del estado de salud para UI
   */
  const getHealthColor = useCallback(() => {
    switch (metrics.systemHealth) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  }, [metrics]);

  /**
   * Obtiene mensaje de estado legible
   */
  const getHealthMessage = useCallback(() => {
    switch (metrics.systemHealth) {
      case 'healthy':
        return 'Sistema OAuth2 funcionando correctamente';
      case 'warning':
        return 'Sistema OAuth2 con advertencias. Monitorear errores.';
      case 'critical':
        return 'Sistema OAuth2 en estado crítico. Revisar configuración.';
      default:
        return 'Estado del sistema OAuth2 desconocido';
    }
  }, [metrics]);

  return {
    // Estado
    metrics,
    diagnostics,
    isHealthy,
    lastUpdate,
    
    // Métodos de utilidad
    updateState,
    exportDiagnostics,
    downloadDiagnostics,
    getStats,
    getRecentErrors,
    getRecentNetworkRequests,
    
    // Verificaciones de estado
    isCritical,
    hasWarnings,
    getHealthColor,
    getHealthMessage
  };
};

export default useOAuth2Monitor;
